import { useMutation, useQueryClient } from '@tanstack/react-query';
import { JsonObject } from 'type-fest';

import { useRequest, type MutationFunction } from '../common';
import { invalidateSubquotesListQuery } from './useGetSubquotesList';

export type UseCreateSubquoteData = {
  groupBusinessName: string;
  groupId: number;
  id: number;
  number: number;
  quoteNumberWithRevision: string;
}[] &
  JsonObject;

export type SubquoteAssignment = {
  groupId: number;
  salespersonId?: number | null;
  estimatorId?: number | null;
};

export type SubquoteContactInfo = {
  email: string;
  firstName?: string;
  lastName?: string;
};

export type UseCreateSubquoteRequestVars = {
  quoteId: number;
  assignments: SubquoteAssignment[];
  contactInfo: SubquoteContactInfo;
  quoteItemIds?: number[];
  quoteFileUuids?: string[];
  notes?: string;
};

export const useCreateSubquote: MutationFunction<
  UseCreateSubquoteData,
  UseCreateSubquoteRequestVars
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, ...body }) =>
      request(`/quotes/${quoteId}/subquote`, {
        query,
        init: {
          method: 'POST',
          body,
        },
      }),
    ...queryOptions,
    onSuccess: (data, variables, context) => {
      // Automatically invalidate the subquotes list for the parent quote
      invalidateSubquotesListQuery(queryClient, variables.quoteId);
      // Call user-provided onSuccess if it exists
      queryOptions?.onSuccess?.(data, variables, context);
    },
  });
};
